{/* Copyright 2026 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. */}

import {Layout} from '../../src/Layout';
export default Layout;
import {FunctionAPI} from '../../src/FunctionAPI';
import docs from 'docs:@react-aria/i18n';

export const section = 'Utilities';
export const description = 'Wraps Intl.ListFormat and formats lists of values for the current locale.';

# useListFormatter

<PageDescription>{docs.exports.useListFormatter.description}</PageDescription>

## Introduction

`useListFormatter` wraps a builtin browser [Intl.ListFormat](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/ListFormat)
object to provide a React Hook that integrates with the i18n system in React Aria. It handles formatting lists of values for the current locale,
updating when the locale changes, and caching of list formatters for performance. See the
[Intl.ListFormat](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/ListFormat) docs for
information on formatting options.

## Example

This example joins a list of values into a single localized string for two locales: USA, and Spain. Two instances of the `List` component are rendered,
using the [I18nProvider](I18nProvider) to specify the locale to display.

```tsx render
'use client';
import {I18nProvider} from 'react-aria/I18nProvider';
import {useListFormatter} from 'react-aria/useListFormatter';

function List({items}) {
  let formatter = useListFormatter({type: 'conjunction'});

  return (
    <p>{formatter.format(items)}</p>
  );
}

<>
  <I18nProvider locale="en-US">
    <List items={['Apples', 'Oranges', 'Bananas']} />
  </I18nProvider>
  <I18nProvider locale="es-ES">
    <List items={['manzanas', 'naranjas', 'plátanos']} />
  </I18nProvider>
</>
```

## API

<FunctionAPI function={docs.exports.useListFormatter} links={docs.links} />
